import { useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import type {
  PromoteChannelRequest,
  Channel,
} from '@weave/schema-gen-ts/dist/schemas/integrations/sync-config-api/v1/message.pb';
import { useListChannelsQuery, usePromoteChannelMutation } from '@frontend/api-sync-config';
import { Button, SpinningLoader, Text, useAlert, useModalControl } from '@frontend/design-system';
import { useAuth } from '../../../providers/auth.provider';
import {
  getPromotionTarget,
  getErrorMessage,
  normalizeChannel,
  orderChannelsForDisplay,
  supportsDirectReplace,
} from '../shared/helpers';
import { ChannelColumn } from './channel-column';
import { CreateChannelModal } from './create-channel-modal';
import { PromoteChannelModal } from './promote-channel-modal';
import { ReplaceTrainModal } from './replace-train-modal';

interface ChannelsPanelProps {
  canManage: boolean;
}

export const ChannelsPanel = ({ canManage }: ChannelsPanelProps) => {
  const { user } = useAuth();
  const alerts = useAlert();
  const promoteModal = useModalControl();
  const replaceModal = useModalControl();
  const createModal = useModalControl();

  const [replacingChannels, setReplacingChannels] = useState<Set<string>>(new Set());
  const [pendingPromotion, setPendingPromotion] = useState<{
    sourceChannel: Channel;
    targetChannelName: string;
  } | null>(null);
  const [pendingReplacement, setPendingReplacement] = useState<Channel | null>(null);

  const {
    data: channelsData,
    isLoading: isChannelsLoading,
    isError: isChannelsError,
    error: channelsError,
  } = useListChannelsQuery({
    request: {},
    httpOptions: { skipValidation: true },
  });

  const { mutate: promoteChannel, isLoading: isPromoting } = usePromoteChannelMutation<Error>({
    httpOptions: { skipValidation: true },
  });

  const { mutate: replaceChannelTrain } = usePromoteChannelMutation<Error>({ httpOptions: { skipValidation: true } });

  const { mutate: createChannel, isLoading: isCreating } = usePromoteChannelMutation<Error>({
    httpOptions: { skipValidation: true },
  });

  const fetchedChannels = useMemo<Channel[]>(() => {
    const channels: Channel[] = [];

    for (const channel of channelsData?.channels ?? []) {
      const normalizedChannel = normalizeChannel(channel);
      if (normalizedChannel) channels.push(normalizedChannel);
    }

    return channels;
  }, [channelsData]);

  const orderedChannels = useMemo(() => orderChannelsForDisplay(fetchedChannels), [fetchedChannels]);

  const existingChannelNames = useMemo(
    () => fetchedChannels.map((channel) => channel.channelName ?? ''),
    [fetchedChannels]
  );

  useEffect(() => {
    if (isChannelsError && channelsData) {
      alerts.error(`Failed to refresh channels: ${getErrorMessage(channelsError)}`);
    }
  }, [alerts, channelsData, channelsError, isChannelsError]);

  const mutationAlertOptions = ({
    successMessage,
    errorPrefix,
    onSuccess,
    onSettled,
  }: {
    successMessage: string;
    errorPrefix: string;
    onSuccess?: () => void;
    onSettled?: () => void;
  }) => ({
    onSuccess: () => {
      onSuccess?.();
      alerts.success(successMessage);
    },
    onError: (error: Error) => {
      alerts.error(`${errorPrefix}: ${getErrorMessage(error)}`);
    },
    onSettled,
  });

  const handlePromoteClick = (sourceChannel: Channel, targetChannelName: string) => {
    if (!canManage) return;

    setPendingPromotion({ sourceChannel, targetChannelName });
    promoteModal.openModal();
  };

  const handleConfirmPromote = (payload: PromoteChannelRequest) => {
    if (!canManage || isPromoting) return;
    promoteChannel(
      { channelName: payload.channelName, trainId: payload.trainId, promotedBy: payload.promotedBy },
      mutationAlertOptions({
        successMessage: `Promoted ${payload.trainId} to ${payload.channelName}`,
        errorPrefix: `Failed to promote to ${payload.channelName}`,
        onSuccess: () => {
          setPendingPromotion(null);
          promoteModal.closeModal();
        },
      })
    );
  };

  const handleReplaceClick = (channel: Channel) => {
    if (!canManage) return;

    setPendingReplacement(channel);
    replaceModal.openModal();
  };

  const handleConfirmReplace = (channelName: string, trainId: string) => {
    if (!canManage || replacingChannels.has(channelName)) return;
    setReplacingChannels((prev) => new Set(prev).add(channelName));

    replaceChannelTrain(
      { channelName, trainId, promotedBy: user?.username ?? 'unknown' },
      mutationAlertOptions({
        successMessage: `${channelName} channel updated to ${trainId}`,
        errorPrefix: `Failed to update ${channelName}`,
        onSuccess: () => {
          setPendingReplacement((current) => (current?.channelName === channelName ? null : current));
          replaceModal.closeModal();
        },
        onSettled: () =>
          setReplacingChannels((prev) => {
            const next = new Set(prev);
            next.delete(channelName);
            return next;
          }),
      })
    );
  };

  const handleConfirmCreate = (channelName: string, trainId: string) => {
    if (!canManage || isCreating) return;

    createChannel(
      { channelName, trainId, promotedBy: user?.username ?? 'unknown' },
      mutationAlertOptions({
        successMessage: `Created ${channelName} channel with ${trainId}`,
        errorPrefix: `Failed to create ${channelName}`,
        onSuccess: createModal.closeModal,
      })
    );
  };

  const isInitialLoad = isChannelsLoading && !channelsData;

  return (
    <div css={wrapperStyle}>
      <div css={headerStyle}>
        <Text as='h2' weight='bold' size='large'>
          Global Deployment Channels
        </Text>
        {canManage && (
          <Button variant='primary' size='large' onClick={() => createModal.openModal()}>
            + Create New Channel
          </Button>
        )}
      </div>

      {isInitialLoad && (
        <div css={statusStyle}>
          <SpinningLoader size='large' />
        </div>
      )}

      {isChannelsError && !channelsData && (
        <div css={statusStyle} role='alert'>
          <Text color='error'>Failed to load channels.</Text>
        </div>
      )}

      <div css={columnsStyle}>
        {orderedChannels.map((channel) => {
          const name = channel.channelName ?? '';
          const promotesTo = getPromotionTarget(name);
          const canReplace = supportsDirectReplace(name);
          return (
            <ChannelColumn
              key={name}
              channelName={name}
              entries={[channel]}
              targetChannelName={promotesTo}
              onPromoteClick={promotesTo ? (sourceChannel) => handlePromoteClick(sourceChannel, promotesTo) : undefined}
              onReplaceClick={canReplace ? handleReplaceClick : undefined}
              isManageDisabled={!canManage}
              isReplacingTrain={canReplace ? replacingChannels.has(name) : undefined}
            />
          );
        })}
      </div>

      <PromoteChannelModal
        modalProps={promoteModal.modalProps}
        sourceChannel={pendingPromotion?.sourceChannel}
        targetChannelName={pendingPromotion?.targetChannelName}
        isSaving={isPromoting}
        onConfirm={handleConfirmPromote}
      />

      <ReplaceTrainModal
        modalProps={replaceModal.modalProps}
        channel={pendingReplacement ?? undefined}
        isSaving={pendingReplacement ? replacingChannels.has(pendingReplacement.channelName ?? '') : false}
        onConfirm={handleConfirmReplace}
      />

      <CreateChannelModal
        modalProps={createModal.modalProps}
        existingChannelNames={existingChannelNames}
        isSaving={isCreating}
        onConfirm={handleConfirmCreate}
      />
    </div>
  );
};

const wrapperStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--fabric-space-gap-lg);
`;

const headerStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--fabric-space-gap-md);
  margin-top: var(--fabric-space-gap-md);
`;

const columnsStyle = css`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: var(--fabric-space-gap-lg);
  align-items: start;
`;

const statusStyle = css`
  display: flex;
  justify-content: center;
  padding: var(--fabric-space-padding-md);
`;
